import { useTranslation } from 'react-i18next';
import Dialog from 'components/Dialogs';
import {
  Button as MuiButton,
  Typography as MuiTypography,
  useTheme,
} from '@mui/material';
import { FormFileUploader } from 'components/ui';
import { Form } from 'react-final-form';
import useImportPatients from './hooks/useImportPatients';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { LoadingButton } from '@mui/lab';

const importPatientMandatoryCSVHeaders = [
  'first_name',
  'last_name',
  'birth_date',
  'email',
];

const importPatientSupportedCSVHeaders = [
  'first_name',
  'middle_name',
  'last_name',
  'name_suffix',
  'email',
  'phone',
  'gender',
  'birth_date',
  'birth_place_country',
  'birth_place_state',
  'birth_place_city',
  'birth_place_address',
  'birth_place_zipcode',
  'cpf',
  'address_country',
  'address_state',
  'address_city',
  'address_address',
  'address_zipcode',
  'care_team_ids',
  'plan_definition_ids',
  'is_mfa_required',
  'preferred_languages',
  'emergency_contact_person_first_name',
  'emergency_contact_person_last_name',
  'emergency_contact_person_email',
  'emergency_contact_person_phone_number',
  'coverage_health_insurance_id',
  'coverage_health_insurance_issuer_name',
  'coverage_health_insurance_beneficiary',
  'coverage_health_insurance_coverage_type',
  'coverage_validity_start_date',
  'coverage_validity_end_date',
  'preferred_time_zone',
  'patient_group_ids',
];

function ImportPatientsDialog(props) {
  const { close } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { onImportPatients } = useImportPatients();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [patientsNumber, setPatientsNumber] = useState(null);

  const parseCSVHeaders = useCallback(
    (file) =>
      new Promise((resolve, _reject) => {
        const reader = new FileReader();
        reader.onload = function (e) {
          const contents = e.target.result;
          const contentsArray = contents.split('\n');
          const csvHeadersArray = contentsArray[0]
            .replace('\r', '')
            .replace(';', ',')
            .split(',');

          const missingHeaders = importPatientMandatoryCSVHeaders.filter(
            (header) => !csvHeadersArray.includes(header)
          );

          const wrongHeaders = csvHeadersArray.filter(
            (header) => !importPatientSupportedCSVHeaders.includes(header)
          );

          const csvHeadersIsValid =
            missingHeaders.length === 0 && wrongHeaders.length === 0;

          if (csvHeadersIsValid) {
            resolve(null);
          } else {
            const missingHeadersString = missingHeaders.length
              ? `\n${t('Missing headers')}: ${missingHeaders.join(', ')}`
              : '';
            const wrongHeadersString = wrongHeaders.length
              ? `\n${t('Wrong headers')}: ${wrongHeaders.join(', ')}`
              : '';
            resolve({
              title: 'Invalid CSV',
              subtitleFirstPart: 'Check the headers of your file.',
              subtitleSecondPart: `${missingHeadersString}${wrongHeadersString}`,
            });
          }
        };
        file && reader.readAsText(file);
      }),
    [t]
  );

  return (
    <Dialog {...props} onClose={close} title={t('Import patients')}>
      <Form
        initialValues={{}}
        onSubmit={(values) => {
          setLoading(true);
          onImportPatients({
            record: { csvFile: values.file[0] },
          })
            .then((res) => {
              if (res?.user?.patient?.invitePatientsWithCsvStatus?.error) {
                setLoading(false);
                enqueueSnackbar(
                  res.user.patient.invitePatientsWithCsvStatus.error,
                  {
                    variant: 'error',
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'center',
                    },
                  }
                );
              } else {
                setLoading(false);
                enqueueSnackbar(t('All patients were successfully imported'), {
                  variant: 'success',
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'center',
                  },
                });
                close();
              }
            })
            .catch((error) => {
              setLoading(false);
              enqueueSnackbar(error?.message || 'An error occured', {
                variant: 'error',
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'center',
                },
              });
            });
        }}
        render={({ handleSubmit, values }) => {
          const file = values?.file?.[0];
          const reader = new FileReader();
          reader.onload = function (e) {
            if (!e) return setPatientsNumber(null);
            const contents = e.target.result;
            return setPatientsNumber(contents.split('\n').length - 1);
          };
          file && reader.readAsText(file);
          return (
            <form onSubmit={handleSubmit}>
              <div style={{ width: 580 }}>
                <FormFileUploader
                  maxFiles={1}
                  singleFile
                  name="file"
                  isInput={false}
                  acceptedTypesProps="text/csv"
                  mainTextArray={['CSV']}
                  customValidation={parseCSVHeaders}
                  onLoadingChange={setFileUploading}
                />
              </div>
              {!file && (
                <MuiTypography
                  variant="h5"
                  component="a"
                  color={theme.palette.colors.blue100}
                  mt={2}
                  display="inline-block"
                  href={`${process.env.REACT_APP_API_HOST}/static/patient-import-template.csv`}
                  download="Patient Import Template"
                  target="_blank"
                >
                  <span>{t('Download CSV template')}</span>
                </MuiTypography>
              )}
              {file && (
                <MuiTypography
                  variant="h5"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '20px',
                  }}
                >
                  <span>{t('Number of patients')}</span>
                  <span>{patientsNumber}</span>
                </MuiTypography>
              )}
              <div
                style={{
                  display: 'flex',
                  marginTop: '30px',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  {/* DO NOT REMOVE, HIDDEN FOR A FIRST VERSION*/}
                  {/* <p style={{ color: colors.gray400 }}>{t('Help center')}</p> */}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <MuiButton
                    variant="outlined"
                    color="tertiary"
                    onClick={close}
                  >
                    {t('Cancel')}
                  </MuiButton>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    disabled={!values?.file?.[0] || loading || fileUploading}
                    loading={loading || fileUploading}
                  >
                    {t('Import')}
                  </LoadingButton>
                </div>
              </div>
            </form>
          );
        }}
      />
    </Dialog>
  );
}

export default ImportPatientsDialog;
